import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import "../css/Login.css";
import "../css/CompleteRegistration.css";
import VelaKoda from "../assets/Images/LoginLogo.svg";
import VelaKodaMobile from "../assets/Images/MobileLogo.svg";
import { showErrorToast, showSuccessToast } from "../utils/toastUtils";

const images = [
  "/img/superadminbg2.png",
  "/img/superadminbg5.png",
  "/img/superadminbg3.png",
  "/img/superadminbg4.png",
];

const CompleteRegistration = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [currentImage, setCurrentImage] = useState(0);
  const [registration, setRegistration] = useState(null);
  const [pageState, setPageState] = useState("validating");
  const [pageMessage, setPageMessage] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const interval = setInterval(
      () => setCurrentImage((value) => (value + 1) % images.length),
      2000,
    );
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let active = true;
    const validateToken = async () => {
      if (!token) {
        setPageState("invalid");
        setPageMessage("This registration link is invalid or expired. Please request a new link.");
        return;
      }
      try {
        const response = await api.get(
          "/auth/complete-registration/validate-token",
          { params: { token }, silent: true, skipAuth: true },
        );
        if (active) {
          setRegistration(response.data?.data || response.data?.registration);
          setPageState("ready");
        }
      } catch (error) {
        if (active) {
          const completed = error.response?.data?.code === "TOKEN_COMPLETED";
          setPageState(completed ? "completed" : "invalid");
          setPageMessage(completed
            ? "This registration has already been completed. Please log in."
            : "This registration link is invalid or expired. Please request a new link.");
        }
      }
    };
    validateToken();
    return () => {
      active = false;
    };
  }, [token]);

  const validateForm = () => {
    const message = !username.trim()
      ? "User Name is required."
      : !password
        ? "Password is required."
        : !confirmPassword
          ? "Confirm Password is required."
          : password !== confirmPassword
            ? "Passwords do not match."
            : !acceptedTerms
              ? "Please accept the terms to continue."
              : "";
    if (message) showErrorToast(message);
    return !message;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading || !validateForm()) return;
    setLoading(true);
    try {
      const response = await api.post(
        "/auth/complete-registration",
        {
          token,
          username: username.trim(),
          password,
          confirmPassword,
          acceptedTerms,
        },
        { silent: true, skipAuth: true },
      );
      const {
        access_token,
        refresh_token,
        session_id,
        role_name,
        user,
        redirect_to,
      } = response.data;
      const storedUser = { ...user, role_name, must_reset_password: false };
      [localStorage, sessionStorage].forEach((storage) => {
        if (access_token) storage.setItem("access_token", access_token);
        if (refresh_token) storage.setItem("refresh_token", refresh_token);
        if (session_id) storage.setItem("session_id", session_id);
        if (role_name) storage.setItem("role_name", role_name);
        if (user) storage.setItem("user", JSON.stringify(storedUser));
      });
      showSuccessToast("Registration completed. Welcome to VelaKoda!");
      navigate(redirect_to || "/user/new", { replace: true });
    } catch (error) {
      const code = error.response?.data?.code;
      if (code === "DUPLICATE_USERNAME") {
        showErrorToast("That username is already in use.");
      } else if (code === "TOKEN_COMPLETED") {
        setPageState("completed");
        setPageMessage("This registration has already been completed. Please log in.");
      } else if (code?.startsWith("TOKEN_")) {
        setPageState("invalid");
        setPageMessage("This registration link is invalid or expired. Please request a new link.");
      } else {
        const responseErrors = error.response?.data?.errors;
        const validationMessage = responseErrors
          ? Object.values(responseErrors).find(Boolean)
          : null;
        showErrorToast(
          validationMessage || "We couldn't create your account. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = Boolean(
    token && username.trim() && password && confirmPassword &&
    password === confirmPassword && acceptedTerms
  );

  const readonlyFields = registration ? [
    ["First Name", registration.firstName],
    ["Last Name", registration.lastName],
    ["Account Type", registration.accountType],
    ...(String(registration.accountType).toLowerCase() === "enterprise"
      ? [["Enterprise Name", registration.enterpriseName]] : []),
    ["Mobile Number", registration.mobileNumber],
    ["Email Address", registration.email],
  ] : [];

  return (
    <main className="login-container complete-registration-page">
      <div className="login-left">
        {images.map((image, index) => (
          <div
            key={image}
            className={`bg-slide ${index === currentImage ? "active" : ""}`}
            style={{ backgroundImage: `url(${image})` }}
          >
            <img src={VelaKoda} alt="VelaKoda" className="slide-logo" />
          </div>
        ))}
      </div>
      <img src={VelaKodaMobile} alt="VelaKoda" className="mobile-logo profile-i" />
      <div className="login-right complete-registration-panel">
        <div className="complete-registration-card">
          {pageState === "validating" && (
            <div className="text-center py-5">
              <div className="spinner-border" role="status" aria-label="Validating registration link" />
              <p className="login-p mt-3">Validating your registration link...</p>
            </div>
          )}
          {(pageState === "invalid" || pageState === "completed") && (
            <div className="text-center py-4">
              <h3 className="mb-3 refresh-prompts fw-bolder">Link unavailable</h3>
              <p className="login-p">{pageMessage}</p>
              <Link to="/" className="custom-btn d-inline-block text-decoration-none mt-2">Log In</Link>
            </div>
          )}
          {pageState === "ready" && (
            <form onSubmit={handleSubmit} noValidate>
              <header className="registration-header">
                <h1>Complete Registration</h1>
                <p>Welcome, {registration?.firstName}. Create your credential to access your workspace.</p>
              </header>
              <div className="registration-grid">
                {readonlyFields.map(([label, value]) => (
                  <label className="registration-field" key={label}>
                    <span>{label}</span>
                    <input
                      value={value || ""}
                      readOnly
                      aria-readonly="true"
                      tabIndex={-1}
                      onMouseDown={(event) => event.preventDefault()}
                    />
                  </label>
                ))}
                <label className="registration-field">
                  <span>User Name</span>
                  <input value={username} onChange={(event) => setUsername(event.target.value)} onBlur={() => !username.trim() && showErrorToast("User Name is required.")} autoComplete="username" />
                </label>
                <label className="registration-field">
                  <span>Password</span>
                  <div className="registration-password">
                    <input type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} onBlur={() => !password && showErrorToast("Password is required.")} autoComplete="new-password" />
                    <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"}><i className={`bi ${showPassword ? "bi-eye" : "bi-eye-slash"}`} /></button>
                  </div>
                </label>
                <label className="registration-field">
                  <span>Confirm Password</span>
                  <input type={showPassword ? "text" : "password"} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} onBlur={() => { if (!confirmPassword) showErrorToast("Confirm Password is required."); else if (password !== confirmPassword) showErrorToast("Passwords do not match."); }} autoComplete="new-password" />
                </label>
              </div>
              <div className="registration-terms">
                <input id="acceptedTerms" type="checkbox" checked={acceptedTerms} onChange={(event) => setAcceptedTerms(event.target.checked)} onBlur={() => !acceptedTerms && showErrorToast("Please accept the terms to continue.")} />
                <label htmlFor="acceptedTerms">I agree to the <Link to="/terms">Terms &amp; Conditions</Link> and <Link to="/terms#privacy">Privacy Policy</Link>.</label>
              </div>
              <button type="submit" className="custom-btn registration-submit" disabled={loading || !isFormValid}>{loading ? "Creating Account..." : "Create Account"}</button>
              <p className="registration-login-link">Already have an account? <Link to="/">Log In</Link></p>
            </form>
          )}
        </div>
      </div>
    </main>
  );
};

export default CompleteRegistration;
